import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePublicFileByGuid } from "@/hooks/usePublicFileByGuid";
import { toast } from "@/hooks/useToast";
import { eventsApi } from "@/lib/humhub-api/events";
import { convertBodyToHtml } from "@/lib/utils/htmlSanitizer";
import {
  buildImagePlaceholderHtml,
  buildTimeUntilEventBadgeHtml,
  hasImageToken as checkImageToken,
  IMAGE_TOKEN,
  imagePlaceholderRegex,
  insertImageToken,
  insertTimeUntilEventToken,
  normalizeNewLines,
  replaceImageTokensWithPlaceholder,
  replaceTimeUntilEventToken,
  TIME_UNTIL_EVENT_TOKEN,
  toStoredHtml,
  toValidationPlainText,
} from "@/lib/utils/reminderEmailBody";

const SUBJECT_LIMIT = 120;
const BODY_LIMIT = 2000;

interface UseReminderEmailFormParams {
  eventId: number;
  eventTitle: string;
  reminderId: number | null;
  scope?: "reminder" | "entry";
  primaryImageGuid?: string | null;
  defaultSubject?: string | null;
  defaultBodyText?: string | null;
  defaultBodyHtml?: string | null;
  onDirtyChange?: (isDirty: boolean) => void;
  timeUntilEventLabel?: string | null;
}

export const useReminderEmailForm = ({
  eventId,
  eventTitle,
  reminderId,
  scope = "reminder",
  primaryImageGuid,
  defaultSubject,
  defaultBodyText,
  defaultBodyHtml,
  onDirtyChange,
  timeUntilEventLabel,
}: UseReminderEmailFormParams) => {
  const { t } = useTranslation();
  const imageDisplayLabel = t("eventManage.reminderEmailImagePlaceholder");
  const toStored = (value: string) =>
    toStoredHtml(value, imagePlaceholderRegex);

  const normalizedDefaultSubject = normalizeNewLines(defaultSubject);
  const initialBodySource = defaultBodyHtml ?? defaultBodyText ?? "";
  const normalizedDefaultBody = normalizeNewLines(initialBodySource);

  const [subject, setSubject] = useState<string>(normalizedDefaultSubject);
  const [bodyStored, setBodyStored] = useState<string>(normalizedDefaultBody);
  const [savedSubject, setSavedSubject] = useState<string>(
    normalizedDefaultSubject,
  );
  const [savedBody, setSavedBody] = useState<string>(normalizedDefaultBody);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hasImageToken, setHasImageToken] = useState<boolean>(
    normalizedDefaultBody.includes(IMAGE_TOKEN),
  );

  const resetFromDefaults = useCallback(() => {
    const normalizedSubject = normalizeNewLines(defaultSubject);
    const normalizedBody = normalizeNewLines(
      defaultBodyHtml ?? defaultBodyText ?? "",
    );

    setSubject(normalizedSubject);
    setBodyStored(normalizedBody);
    setSavedSubject(normalizedSubject);
    setSavedBody(normalizedBody);
    setHasImageToken(checkImageToken(normalizedBody));
  }, [defaultBodyHtml, defaultBodyText, defaultSubject]);

  // default 値が変わったときのリセット
  useEffect(() => {
    resetFromDefaults();
  }, [resetFromDefaults]);

  const subjectPlaceholder = t(
    "eventManage.reminderEmailDefaultSubjectPlaceholder",
    {
      eventTitle,
    },
  );
  const bodyPlaceholder = normalizeNewLines(
    t("eventManage.reminderEmailDefaultBodyPlaceholder", { eventTitle }),
  );

  const previewSubject =
    subject.trim().length > 0 ? subject : subjectPlaceholder;
  const previewBody =
    bodyStored.trim().length > 0 ? bodyStored : bodyPlaceholder;

  // usePublicFileByGuidフックを使用してイベント画像を取得
  const { fileBlobUrl: primaryImageSrc } =
    usePublicFileByGuid(primaryImageGuid);

  const placeholderBadgeHtml = useMemo(
    () => buildImagePlaceholderHtml(primaryImageSrc, imageDisplayLabel),
    [imageDisplayLabel, primaryImageSrc],
  );

  const timeUntilBadgeHtml = useMemo(
    () =>
      timeUntilEventLabel && timeUntilEventLabel.trim().length > 0
        ? buildTimeUntilEventBadgeHtml(timeUntilEventLabel)
        : null,
    [timeUntilEventLabel],
  );

  const toDisplay = useMemo(
    () => (value: string) => {
      let next = replaceImageTokensWithPlaceholder(value, placeholderBadgeHtml);
      if (timeUntilBadgeHtml) {
        next = replaceTimeUntilEventToken(next, timeUntilBadgeHtml);
      }
      return next;
    },
    [placeholderBadgeHtml, timeUntilBadgeHtml],
  );
  const editorBodyHtml = useMemo(
    () => toDisplay(convertBodyToHtml(bodyStored)),
    [bodyStored, toDisplay],
  );

  const sendBodyHtml = useMemo(
    () => convertBodyToHtml(previewBody),
    [previewBody],
  );
  const previewBodyText = useMemo(() => {
    const removePlaceholder = (value: string) => toValidationPlainText(value);
    if (bodyStored.trim().length > 0) {
      return removePlaceholder(bodyStored);
    }
    return removePlaceholder(bodyPlaceholder);
  }, [bodyPlaceholder, bodyStored]);
  const hasChanges = subject !== savedSubject || bodyStored !== savedBody;

  useEffect(() => {
    onDirtyChange?.(hasChanges);
  }, [hasChanges, onDirtyChange]);

  const updateField = (target: "subject" | "body", value: string) => {
    const currentValue = target === "subject" ? subject : bodyStored;
    if (value === currentValue) return value;
    if (target === "subject") {
      setSubject(value);
      return value;
    }
    setBodyStored(value);
    return value;
  };

  const handleSubjectChange = (value: string) => {
    updateField("subject", value);
  };

  const handleBodyChange = (value: string) => {
    const stored = toStored(value);
    setHasImageToken(checkImageToken(stored));
    updateField("body", stored);
  };

  const handleInsertImagePlaceholder = () => {
    const next = insertImageToken(bodyStored);
    setBodyStored(next);
    setHasImageToken(true);
  };

  const handleInsertTimeUntilEventPlaceholder = () => {
    const next = insertTimeUntilEventToken(bodyStored);
    setBodyStored(next);
  };

  const buildEmailPayload = useCallback(() => {
    const trimmedSubject = subject.trim();
    const plainBodyForValidation = toValidationPlainText(sendBodyHtml);
    const validationError = !trimmedSubject
      ? t("eventManage.reminderEmailSubjectRequired")
      : !plainBodyForValidation
        ? t("eventManage.reminderEmailBodyRequired")
        : trimmedSubject.length > SUBJECT_LIMIT
          ? t("eventManage.reminderEmailCharacterCount", {
              count: trimmedSubject.length,
              max: SUBJECT_LIMIT,
            })
          : plainBodyForValidation.length > BODY_LIMIT
            ? t("eventManage.reminderEmailCharacterCount", {
                count: plainBodyForValidation.length,
                max: BODY_LIMIT,
              })
            : null;

    return {
      validationError,
      payload: {
        subject: trimmedSubject,
        bodyHtml: sendBodyHtml,
        bodyText: previewBodyText,
      },
    };
  }, [previewBodyText, sendBodyHtml, subject, t]);

  const sendEmailParticipants = async ({
    sendNow = false,
    testMode = false,
  }: {
    sendNow?: boolean;
    testMode?: boolean;
  }) => {
    if (isSubmitting) return;
    if (scope === "reminder" && !reminderId) {
      toast({
        title: t("common.error"),
        description: t("eventManage.reminderEmailNoSchedules"),
        variant: "error",
      });
      return;
    }

    const { validationError, payload } = buildEmailPayload();
    if (validationError) {
      toast({
        title: t("common.error"),
        description: validationError,
        variant: "error",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (scope === "entry" && !sendNow && !testMode) {
        await eventsApi.updateEvent(eventId, {
          CalendarEntryForm: {
            email_subject_custom: payload.subject,
            email_body_html_custom: payload.bodyHtml,
            email_body_text_custom: payload.bodyText,
            scope: "single",
          },
        });
      } else {
        await eventsApi.sendReminderEmail(eventId, {
          reminderId:
            scope === "reminder" ? (reminderId ?? undefined) : undefined,
          email_subject: payload.subject,
          email_body_html: payload.bodyHtml,
          email_body_text: payload.bodyText,
          save_for_reminder: scope === "reminder",
          save_for_entry: scope === "entry",
          send_now: sendNow,
          test_mode: testMode,
        });
      }
      setSavedSubject(subject);
      setSavedBody(bodyStored);
      toast({
        title: t("common.success"),
        description: sendNow
          ? testMode
            ? t("eventManage.reminderEmailSendTest")
            : t("eventManage.reminderEmailSendNow")
          : t("eventManage.reminderEmailUpdated"),
      });
    } catch (error) {
      toast({
        title: t("common.error"),
        description:
          error instanceof Error ? error.message : t("common.tryAgainLater"),
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = () =>
    sendEmailParticipants({ sendNow: false, testMode: false });
  const handleSendNow = () =>
    sendEmailParticipants({ sendNow: true, testMode: false });
  const handleSendTest = () =>
    sendEmailParticipants({ sendNow: true, testMode: true });

  return {
    // 状態
    subject,
    reminderId,
    hasImageToken,
    isSubmitting,
    hasChanges,
    primaryImageSrc,
    placeholders: {
      subject: subjectPlaceholder,
      body: bodyPlaceholder,
      imageHtml: placeholderBadgeHtml,
      timeUntilEventHtml: timeUntilBadgeHtml ?? TIME_UNTIL_EVENT_TOKEN,
    },
    preview: {
      subject: previewSubject,
      bodyHtml: editorBodyHtml,
    },
    payload: {
      bodyHtml: sendBodyHtml,
    },
    // ハンドラ
    handleSubjectChange,
    handleBodyChange,
    handleInsertImagePlaceholder,
    handleInsertTimeUntilEventPlaceholder,
    handleSave,
    handleSendNow,
    handleSendTest,
    SUBJECT_LIMIT,
    BODY_LIMIT,
    resetFromDefaults,
    buildEmailPayload,
    markAsSaved: () => {
      setSavedSubject(subject);
      setSavedBody(bodyStored);
    },
  };
};
